// Pure chart maths, kept out of the view so it can be tested without pulling in
// the API client. Same reasoning as the server's decide.js: the part that can be
// wrong quietly is the part that should be directly testable.

// Pixels available to the tallest bar, once the month label below it has taken
// its share. Nothing sits above the bar any more - the per-month values moved
// into the card header - so this is now the whole plot area.
export const CHART_HEIGHT = 90;

// Above this many bars the months stop being labelled individually. Twelve
// three-letter labels across a 375px phone works out at ~19px of column for a
// ~22px word, so they collide. See LABEL_EVERY.
const LABEL_ALL_UP_TO = 6;

// Every third month gets a tick when the series is too dense to label in full,
// anchored to the newest month so "where are we now" is always named.
const LABEL_EVERY = 3;

/**
 * Turn the monthly-counts response into bars, oldest month first.
 *
 * Two things here have already been got wrong once:
 *
 *   - The endpoint orders newest month first. Taking the tail of the raw list
 *     shows the twelve OLDEST months, labelled backwards.
 *   - pg returns COUNT() as a string, so values need coercing before any
 *     arithmetic. "71" > "8" is false.
 *
 * Heights come out in pixels rather than percentages. A percentage on a flex
 * item resolves before its siblings are laid out, and every bar then shrinks to
 * the same leftover space, which draws twelve identical bars.
 *
 * `showLabel` is decided here rather than in the view because it depends on how
 * many months actually came back, which is not twelve until October 2026 - the
 * server clamps to a November 2025 tracking floor.
 */
export function buildChartSeries(monthlyCounts, months = 12) {
  const points = (monthlyCounts || [])
    .filter((point) => point && point.month)
    .map((point) => ({
      month: point.month,
      value: Number(point.member_count ?? point.count ?? 0) || 0
    }))
    .sort((a, b) => new Date(a.month) - new Date(b.month))
    .slice(-months);

  const max = Math.max(...points.map((p) => p.value), 1);
  const last = points.length - 1;

  return points.map((point, index) => ({
    ...point,
    // Floored so a quiet month is still a bar rather than a hairline.
    height: Math.max(8, Math.round((point.value / max) * CHART_HEIGHT)),
    showLabel: points.length <= LABEL_ALL_UP_TO || (last - index) % LABEL_EVERY === 0
  }));
}
